import { RowOptions } from "./tableTypes";

const easternTimeZone = "America/New_York";
const STORAGE_KEY = "cs15-sim-date";
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Dispatched after the spoofed date changes so the UI can re-check release locks. */
export const SIM_DATE_CHANGE_EVENT = "cs15-sim-date-change";

/**
 * Navbar date picker for previewing release locks.
 * Flip to true only while developing locally; leave false for the live site.
 */
export const DATE_PICKER_ENABLED = false;

/** Mid-semester preview date used while developing locally. */
export const DEFAULT_SIMULATED_DATE = "2026-10-22";

const canShowDatePicker =
  DATE_PICKER_ENABLED && process.env.NODE_ENV !== "production";

function readStoredSimulatedDate(): string | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "real") {
      return null;
    }
    if (stored && YMD.test(stored)) {
      return stored;
    }
  } catch {
    // Ignore missing window/localStorage (tests, SSR).
  }

  return canShowDatePicker ? DEFAULT_SIMULATED_DATE : null;
}

let simulatedDate: string | null = readStoredSimulatedDate();

function notifySimDateChange(): void {
  try {
    window.dispatchEvent(
      new CustomEvent(SIM_DATE_CHANGE_EVENT, { detail: simulatedDate })
    );
  } catch {
    // Ignore missing window (tests, SSR).
  }
}

/** YYYY-MM-DD being simulated, or null to use the real Eastern clock. */
export function getSimulatedDate(): string | null {
  return simulatedDate;
}

export function setSimulatedDate(ymd: string | null): void {
  simulatedDate = ymd;

  try {
    if (ymd === null) {
      window.localStorage.setItem(STORAGE_KEY, "real");
    } else {
      window.localStorage.setItem(STORAGE_KEY, ymd);
    }
  } catch {
    // Ignore quota / privacy-mode failures.
  }

  notifySimDateChange();
}

export function canSimulateDate(): boolean {
  return canShowDatePicker;
}

function formatEasternYmd(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: easternTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  if (!year || !month || !day) {
    return "";
  }
  return `${year}-${month}-${day}`;
}

function parseSpoofDate(input: string | Date): string | null {
  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) {
      return null;
    }
    return formatEasternYmd(input);
  }

  const trimmed = input.trim();
  if (YMD.test(trimmed)) {
    return trimmed;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return formatEasternYmd(parsed);
}

function currentClockMessage(): string {
  return simulatedDate
    ? `Spoofing Eastern date as ${simulatedDate} (end of day). Call resetDate() to use real time.`
    : "Using real Eastern time. Call spoofDate(\"2026-10-22\") to preview release locks.";
}

/**
 * Console helper: spoof the site clock used for lecture/assignment locks.
 * Examples: spoofDate("2026-11-15")  spoofDate("Nov 15, 2026")  spoofDate()
 */
export function spoofDate(input?: string | Date): string {
  if (input === undefined) {
    return currentClockMessage();
  }

  if (typeof input === "string" && /^(now|real|reset)$/i.test(input.trim())) {
    return resetDate();
  }

  const ymd = parseSpoofDate(input);
  if (!ymd) {
    return 'Could not parse that date. Try spoofDate("2026-10-22") or spoofDate("Oct 22, 2026").';
  }

  setSimulatedDate(ymd);
  return `Spoofing Eastern date as ${ymd} (end of day). Assignments and lectures now use this date. Call resetDate() to go back.`;
}

/** Console helper: stop spoofing and use the real Eastern clock. */
export function resetDate(): string {
  setSimulatedDate(null);
  return "Back to real Eastern time.";
}

interface Cs15Console {
  spoofDate: typeof spoofDate;
  resetDate: typeof resetDate;
  help: () => string;
}

function installDateSpoofConsole(): void {
  if (typeof window === "undefined") {
    return;
  }

  const api: Cs15Console = {
    spoofDate,
    resetDate,
    help: () =>
      [
        "Secret date spoof (release locks only, this browser):",
        '  spoofDate("2026-11-15")',
        '  spoofDate("Nov 15, 2026")',
        "  spoofDate()            // current clock",
        "  resetDate()            // real time",
      ].join("\n"),
  };

  window.cs15 = api;
  window.spoofDate = spoofDate;
  window.resetDate = resetDate;
}

declare global {
  interface Window {
    cs15: Cs15Console;
    spoofDate: typeof spoofDate;
    resetDate: typeof resetDate;
  }
}

installDateSpoofConsole();

/**
 * End of the simulated calendar day in Eastern time, so picking a date means
 * "everything that should have come out by the end of that day."
 */
function simulatedMoment(ymd: string): Date {
  const [year, month, day] = ymd.split("-").map(Number);
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-US", {
    month: "short",
  });
  const tz = isEasternDaylightTime(year, month, day) ? "EDT" : "EST";
  return new Date(`${monthName} ${day}, ${year} 23:59:59 ${tz}`);
}

function nthWeekdayOfMonth(
  year: number,
  monthIndex: number,
  weekday: number,
  n: number
): Date {
  const first = new Date(year, monthIndex, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, monthIndex, 1 + offset + (n - 1) * 7);
}

/** US Eastern DST: 2nd Sunday of March through the first Sunday of November. */
function isEasternDaylightTime(year: number, month: number, day: number): boolean {
  const date = new Date(year, month - 1, day);
  const start = nthWeekdayOfMonth(year, 2, 0, 2);
  const end = nthWeekdayOfMonth(year, 10, 0, 1);
  return date >= start && date < end;
}

function currentEasternTime(): Date {
  if (simulatedDate !== null) {
    return simulatedMoment(simulatedDate);
  }

  return new Date(
    new Date().toLocaleString("en-US", { timeZone: easternTimeZone })
  );
}

/** Whether a row's links should be visible at the current time. */
export function isReleased(row: Pick<RowOptions, "autoReleaseDate">): boolean {
  return (
    row.autoReleaseDate === undefined ||
    currentEasternTime() > new Date(row.autoReleaseDate)
  );
}

/** Whether an assignment is past its due date. */
export function isPastDue(row: Pick<RowOptions, "dueDate">): boolean {
  return row.dueDate !== undefined && currentEasternTime() > new Date(row.dueDate);
}

type ScheduleRow = Pick<RowOptions, "autoReleaseDate" | "dueDate">;

/** The latest released assignment that is still in progress. */
export function isCurrentProject(
  row: ScheduleRow,
  rows: ScheduleRow[]
): boolean {
  if (!isReleased(row) || isPastDue(row) || row.autoReleaseDate === undefined) {
    return false;
  }

  const latest = rows.reduce((max, candidate) => {
    if (
      !isReleased(candidate) ||
      isPastDue(candidate) ||
      candidate.autoReleaseDate === undefined
    ) {
      return max;
    }
    return Math.max(max, new Date(candidate.autoReleaseDate).getTime());
  }, -1);

  return new Date(row.autoReleaseDate).getTime() === latest;
}

/** Returns a copy of a row with all resource links hidden. */
export function hideRowLinks(row: RowOptions): RowOptions {
  const cellNametoLinks = new Map<string, string>();

  row.cellNametoLinks.forEach((_, label) => cellNametoLinks.set(label, ""));

  return { ...row, cellNametoLinks };
}
